import type { OzonDirection } from "@/api/ozonSupply";
import {
  STEP_LABEL,
  applying,
  editing,
  failed,
  type CascadeStep,
} from "./changeModel";

/** На каком шаге цикла стоит поставка — ОДНА чистая функция на весь раздел.
 *
 *  Шаги заданы необратимостью: черновик (обратим) → заявка (отменяема) → раскладка (счёт) →
 *  грузоместа (коды выдаются ОДИН раз) → 1С (ПТМО переводит товар на юрлицо) → сборка. Пока
 *  «где мы» считалось лесенкой `if` прямо в разметке, каждая кнопка решала это по-своему — и
 *  «Раскладка» однажды висела там, где раскладывать было ещё нечего.
 *
 *  ⚠️ Стадия — не бейдж состояния. `StateBadge` показывает СЛОВО ПЛОЩАДКИ (не везут, сорвалось),
 *  а стадия отвечает на вопрос «что делать дальше», и у отказа площадки ответ тот же, что у
 *  нетронутого направления: спросить заново. Поэтому `refused`/`failed` — это `ask`.
 */
export type OzonStage =
  /** не спрашивали площадку (или она отказала/сорвалась — делать надо то же) */
  | "ask"
  /** черновик снят и жив: площадка назвала потолок, можно брать окно */
  | "draft"
  /** черновику больше 25 минут: ни окон, ни заявки по нему не дадут */
  | "draft_stale"
  /** заявка отправлена, ответа ещё нет — проверяют кабинет */
  | "ordering"
  /** заявка создана, раскладки нет — разложить */
  | "order"
  /** разложено, на площадку не уехало ни одно место */
  | "layout"
  /** часть мест стоит на площадке, часть нет — дослать */
  | "layout_partial"
  /** все места на площадке, в 1С ещё ничего — записать */
  | "sent"
  /** цикл 1С начат и не доведён до конца — продолжить */
  | "onec_partial"
  /** бумаги готовы: печать этикеток и сборка */
  | "assembly"
  /** идёт правка состава после заявки — она главнее всего остального */
  | "change";

/** Секции страницы поставки. Те же разделы, что раскрывались в строке списка, плюс `change` —
 *  превью правки состава (её экран — Э-8, пока раздел честно говорит, что ещё не сделан). */
export type OzonTab = "content" | "slots" | "places" | "onec" | "change";

/** Сколько живёт наш черновик. Площадка держит его 30 минут, портал считает своим 25
 *  (`ozon_directions.DRAFT_TTL`): по протухшему не дадут ни окон, ни заявки. */
export const DRAFT_TTL_MIN = 25;

/** Протух ли снятый черновик. `now` параметром — чтобы стадию можно было проверить тестом, а не
 *  ждать двадцать пять минут. */
export function draftStale(
  d: OzonDirection,
  now: number = Date.now(),
): boolean {
  if (d.state !== "drafted" || !d.drafted_at) return false;
  return (now - new Date(d.drafted_at).getTime()) / 60_000 > DRAFT_TTL_MIN;
}

export function stageOf(d: OzonDirection, now: number = Date.now()): OzonStage {
  // правка состава перебивает всё: пока она идёт, половина обычных кнопок откажет, и говорить
  // «отправьте места» человеку, который переделывает раскладку, — врать
  if (d.change) return "change";
  if (d.state === "ordering") return "ordering";
  if (d.state === "drafted")
    return draftStale(d, now) ? "draft_stale" : "draft";
  if (d.state !== "ordered") return "ask";
  // дальше — жизнь заявки. Числа мест считает бэкенд и только по НЕПУСТЫМ местам: пустое на
  // площадку не едет и кода не получит никогда
  if (d.places_total === 0) return "order";
  if (d.places_sent === 0) return "layout";
  if (d.places_sent < d.places_total) return "layout_partial";
  if (!d.onec) return "sent";
  return d.onec.state === "done" ? "assembly" : "onec_partial";
}

/** Какую секцию открыть, если в адресе её не назвали: ту, в которой стоит работа.
 *
 *  У протухшего черновика это СОСТАВ, а не окна: окон по нему не дадут, а спрашивать площадку
 *  заново идут, посмотрев на количества.
 */
export function defaultTab(stage: OzonStage): OzonTab {
  switch (stage) {
    case "ask":
    case "draft_stale":
    case "ordering":
      return "content";
    case "draft":
      return "slots";
    case "onec_partial":
    case "assembly":
      return "onec";
    default:
      return "places";
  }
}

/* ────────────────────────── прогресс стадий ────────────────────────── */

/** Шесть шагов поставки — те же, что человек проходит руками, и в том же порядке. */
export const STEPS = [
  "черновик",
  "заявка",
  "раскладка",
  "места на площадке",
  "1С",
  "сборка",
] as const;

/** На каком из шести шагов СТОИТ РАБОТА. ⚠️ Не «что сделано»: у стадии `order` заявка уже
 *  создана, но работа стоит на раскладке — её шаг и подсвечен. */
export function stepOf(stage: OzonStage): number {
  switch (stage) {
    case "ask":
    case "draft":
    case "draft_stale":
      return 0;
    case "ordering":
      return 1;
    case "order":
    case "change":
      return 2;
    case "layout":
    case "layout_partial":
      return 3;
    case "sent":
    case "onec_partial":
      return 4;
    case "assembly":
      return 5;
  }
}

/** Одна фраза «что дальше» — над прогрессом. Она же объясняет, почему главная кнопка именно эта
 *  (а на `ordering` — почему кнопки нет вовсе). */
export function nextStep(stage: OzonStage, d: OzonDirection): string {
  switch (stage) {
    case "ask":
      return "Черновик не снят: спросите площадку, сколько она возьмёт. Обратимо, в кабинете не видно.";
    case "draft":
      return "Площадка ответила — выберите окно приёмки: именно выбор окна создаёт заявку.";
    case "draft_stale":
      return "Ответ площадки устарел: по нему не дадут ни окон, ни заявки — спросите заново.";
    case "ordering":
      return "Заявка ушла на площадку, ответа не было — нажмите «Обновить»: портал сверит её с кабинетом.";
    case "order":
      return "Заявка создана — разложите согласованный состав по грузоместам.";
    case "layout":
      return "Раскладка есть, на площадку не уехало ни одно место — отправьте их и получите коды.";
    case "layout_partial":
      return `На площадке ${d.places_sent} мест из ${d.places_total} — дошлите остальные: документы 1С пишутся по составу целиком.`;
    case "sent":
      return "Все места стоят на площадке, состав подтверждён — запишите поставку в 1С.";
    case "onec_partial":
      return `Цикл 1С остановился на шаге «${d.onec?.state ?? "?"}» — продолжите запись.`;
    case "assembly":
      return "Бумаги готовы — печатайте этикетки и собирайте.";
    case "change": {
      const ch = d.change;
      if (!ch) throw new Error("стадия «изменение» без записи изменения");
      if (failed(ch))
        return `Применение сорвалось на шаге «${STEP_LABEL[ch.state as CascadeStep]}» — прочитайте причину и продолжите: каскад возобновится с него.`;
      if (applying(ch))
        return `Идёт применение: шаг «${STEP_LABEL[ch.state as CascadeStep]}». Площадка и 1С правятся прямо сейчас — дождитесь конца.`;
      return "Идёт правка состава: соберите целевую раскладку и посмотрите, что изменится.";
    }
  }
}

/* ────────────────────────── действия стадии ────────────────────────── */

/** Что можно сделать с поставкой. Ключ — то, на что экран вешает свой обработчик.
 *
 *  ⚠️ Таблица «стадия → действия» обязана быть ОДНА и проверяться тестом. Пока каждая кнопка
 *  решала сама, показываться ей или нет, над раскладкой стояло до десяти кнопок в ряд, и какая
 *  из них сейчас нужная — не читалось.
 */
export type ActionKey =
  | "draft"
  | "slots"
  /** сверить с площадкой заявку, ответа по которой не было */
  | "resolve"
  | "compute"
  | "recompute"
  | "send"
  | "labels"
  | "pallets"
  | "packing"
  | "packing_xlsx"
  | "onec_run"
  | "onec_reset"
  | "reissue"
  | "cancel"
  | "change"
  /** открыть правку состава, уже согласованного площадкой */
  | "change_start"
  /** отменить неприменённую правку и вернуть раскладку как было */
  | "change_abort"
  /** применить каскад (или продолжить его с сорвавшегося шага) */
  | "change_apply"
  /** этикетки ТОЛЬКО мест, которые завела применённая правка */
  | "labels_changed";

export interface Action {
  key: ActionKey;
  label: string;
  /** зачем и что случится — в `title` кнопки или пункта меню */
  hint?: string;
  /** Почему действие сейчас недоступно — короткой подписью рядом с названием.
   *
   *  ⚠️ Пункт остаётся на месте, но не нажимается: пропади он совсем — экран не сказал бы, чего
   *  не хватает, и «где кнопка отправки?» пришлось бы выяснять у соседа.
   */
  disabled?: string;
  /** пункт печати: в меню они идут одной группой */
  print?: boolean;
  /** Второй формат ТОГО ЖЕ документа — рисуется в той же строке меню.
   *
   *  ⚠️ Отдельными пунктами это два соседа с одинаковым началом («Сборочный лист», «Лист в
   *  Excel»), и выбор формата читается как выбор документа. Скачивать оба разом нельзя: второй
   *  файл браузер режет как непрошеную загрузку. */
  alt?: Action;
}

/** Главное действие стадии и всё остальное — под «…». */
export interface StageActions {
  primary: Action | null;
  more: Action[];
}

/** Раскладка в двух словах — ровно то, от чего зависит НАБОР действий.
 *
 *  Считается по местам, а не по направлению: `places_total`/`places_sent` знают только про
 *  непустые места, а печать наклеек палет и «Разложить заново» смотрят ещё и на транспортные
 *  коды и на пустые места. Нет раскладки на руках (страница ещё не спрашивала её) — `null`, и
 *  пункты, которым она нужна, просто не показываются: врать про несуществующие палеты нельзя.
 */
export interface PlacesInfo {
  /** мест в раскладке всего, включая пустые */
  total: number;
  /** непустых мест: только они едут на площадку и получают коды */
  sendable: number;
  /** непустых мест с кодом площадки */
  coded: number;
  /** есть места с транспортным кодом — только по ним печатаются наклейки палет */
  pallets: boolean;
}

const act = (
  key: ActionKey,
  label: string,
  hint?: string,
  print = false,
): Action => ({
  key,
  label,
  hint,
  print,
});

const DRAFT_HINT =
  "Снять черновик: узнать у площадки, сколько она возьмёт. Обратимо, в кабинете не видно.";

/** Что делать с поставкой на этой стадии: одно главное действие и остальное под «…».
 *
 *  Чистая функция — вся перекомпоновка кнопок живёт здесь, а не в разметке. `places` — то, что
 *  экран уже знает о раскладке; `null` значит «не спрашивали», и пункты, которым она нужна,
 *  не показываются.
 */
/** Чего экран НЕ смог прочитать. Пустые поля — всё в порядке.
 *
 *  ⚠️ Это не то же, что `places === null`. `null` значит «не спрашивали», и пункты, которым
 *  раскладка нужна, просто не показываются. «Не прочиталась» — другое: места на площадке,
 *  возможно, и есть, а экран про них не знает, и кнопка обязана назвать причину, а не звать
 *  отправлять вслепую.
 */
export interface LayoutTrouble {
  /** раскладка не пришла — текст ошибки запроса */
  places?: string | null;
  /** чек-лист площадки не пришёл — текст ошибки запроса */
  rules?: string | null;
}

/** Действиям из этого списка нужна прочитанная раскладка: без неё они считают по пустоте. */
const NEEDS_PLACES: ActionKey[] = [
  "compute",
  "recompute",
  "send",
  "labels",
  "pallets",
  "packing",
  "packing_xlsx",
  "reissue",
  "onec_run",
];

/** Погасить действие с причиной, если то, на чём оно стоит, не прочиталось. */
function blocked(a: Action, t: LayoutTrouble): Action {
  if (a.disabled) return a;
  if (t.places && NEEDS_PLACES.includes(a.key))
    return { ...a, disabled: "раскладка не прочиталась" };
  // ⚠️ отправка мест необратима — коды выдаются ОДИН раз, и чек-лист площадки единственный
  // говорит, всё ли она требует. Не прочитался — молчать о нём перед отправкой нельзя
  if (t.rules && a.key === "send")
    return { ...a, disabled: "чек-лист площадки не прочитался" };
  return a;
}

/** Что экран знает о превью правки — ровно то, от чего зависит ГЛАВНАЯ кнопка стадии `change`.
 *
 *  ⚠️ «Применить» предлагается только на открытом превью: применение снимает коды с мест и
 *  делает напечатанные этикетки мусором, и нажать его вслепую, не посмотрев дельту, нельзя.
 */
export interface ChangeCtx {
  /** раздел «Что изменится» раскрыт */
  preview: boolean;
  /** почему применять нельзя (`changeModel.applyBlock`); `null` — можно */
  blocked: string | null;
}

export function actionsFor(
  stage: OzonStage,
  d: OzonDirection,
  places: PlacesInfo | null = null,
  /** ошибки запросов раскладки и чек-листа: с ними кнопки не исчезают, а называют причину */
  trouble: LayoutTrouble = {},
  /** превью правки; на стадии `change` без него главной кнопкой остаётся «Показать, что изменится» */
  change: ChangeCtx = { preview: false, blocked: "превью ещё не посчитано" },
): StageActions {
  const a = stageActions(stage, d, places, change);
  if (!trouble.places && !trouble.rules) return a;
  return {
    primary: a.primary ? blocked(a.primary, trouble) : null,
    more: a.more.map((x) => blocked(x, trouble)),
  };
}

function stageActions(
  stage: OzonStage,
  d: OzonDirection,
  places: PlacesInfo | null,
  ctx: ChangeCtx,
): StageActions {
  switch (stage) {
    case "ask":
      return {
        primary: act("draft", "Спросить площадку", DRAFT_HINT),
        more: [],
      };
    case "draft":
      return {
        primary: act(
          "slots",
          "Выбрать окно → заявка",
          "Окна приёмки площадки. Выбор интервала СОЗДАЁТ заявку — она видна в кабинете, но отменяется.",
        ),
        // живой черновик пересняли бы зря: окна по нему ещё дают
        more: [act("draft", "Спросить заново", DRAFT_HINT)],
      };
    case "draft_stale":
      return { primary: act("draft", "Спросить заново", DRAFT_HINT), more: [] };
    case "ordering":
      // ⚠️ главной кнопки нет намеренно: заявка уже ушла, и повторное создание завело бы вторую
      return {
        primary: null,
        more: [
          act(
            "resolve",
            "Обновить",
            "Сверить с площадкой: найдётся заявка — привяжем её, не найдётся — вернём направление к «черновик снят». Ничего не создаёт.",
          ),
        ],
      };
    case "order":
      return {
        primary: act(
          "compute",
          "Разложить",
          "Разложить согласованный состав по грузоместам: только счёт, наружу ничего не уходит",
        ),
        more: rest(stage, d, places),
      };
    case "layout":
      return { primary: sendAction(d), more: rest(stage, d, places) };
    case "layout_partial":
      return { primary: sendAction(d), more: rest(stage, d, places) };
    case "sent":
      return {
        primary: act(
          "onec_run",
          "Записать в 1С",
          "Заказ покупателя с резервом, паллеты, ПТМО и счёт-фактура. ПТМО переводит товар на юрлицо.",
        ),
        more: rest(stage, d, places),
      };
    case "onec_partial":
      return {
        primary: act(
          "onec_run",
          "Продолжить запись в 1С",
          "Дописать документы с того шага, на котором цикл остановился",
        ),
        more: rest(stage, d, places),
      };
    case "assembly":
      return { primary: LABELS, more: rest(stage, d, places) };
    case "change":
      return { primary: changePrimary(d, ctx), more: rest(stage, d, places) };
  }
}

/** Главная кнопка режима изменений: посмотреть → применить → продолжить после сбоя.
 *
 *  ⚠️ Три состояния захода, и подменять их друг другом нельзя. Пока каскад ИДЁТ, кнопки нет
 *  вовсе (второй `apply` бэкенд отсечёт, но человеку надо видеть, что идёт именно применение);
 *  сорвавшийся шаг зовёт ПРОДОЛЖИТЬ — новый заход тут не начинают, каскад возобновляется с него.
 */
function changePrimary(d: OzonDirection, ctx: ChangeCtx): Action {
  const ch = d.change;
  // ⚠️ громко: стадию `change` ставит только наличие записи (`stageOf`), и её отсутствие здесь
  // означает, что экран считает стадию по чему-то другому — молчаливая заглушка спрятала бы это
  if (!ch) throw new Error("стадия «изменение» без записи изменения");
  if (failed(ch))
    return act(
      "change_apply",
      "Продолжить применение",
      `Каскад сорвался на шаге «${STEP_LABEL[ch.state as CascadeStep]}» и продолжится с него же: ` +
        "что уже сделано, портал сверяет у площадки, а не делает второй раз",
    );
  if (applying(ch))
    return {
      ...act("change_apply", "Применение идёт"),
      disabled: `шаг «${STEP_LABEL[ch.state as CascadeStep]}»`,
    };
  if (!ctx.preview)
    return act(
      "change",
      "Показать, что изменится",
      "Дельта по площадке, 1С и печати до того, как что-то уедет наружу",
    );
  const apply = act(
    "change_apply",
    "Применить изменения",
    "Состав заявки → снятие кодов у опустевших мест → досылка новых → правка документов 1С на " +
      "месте → бумаги новой версии. НЕОБРАТИМО со второго шага.",
  );
  return ctx.blocked ? { ...apply, disabled: ctx.blocked } : apply;
}

/** Пока правка не применена, бумаги и учёт показывают ПРЕЖНИЙ состав. */
const STALE = "устарело: изменения не применены";

/** Печать и запись в 1С в режиме изменений — ПОГАШЕНЫ С ПРИЧИНОЙ, а не спрятаны.
 *
 *  ⚠️ Бэкенд их всё равно отвергнет (`ozon_onec.run` отказывает при активном заходе, а
 *  напечатанная сейчас этикетка уедет на короб, которого после каскада не будет). Исчезни они с
 *  экрана — человек искал бы пропавшую кнопку вместо того, чтобы прочитать, почему она не та.
 */
function staleWhileChanging(
  d: OzonDirection,
  places: PlacesInfo | null,
): Action[] {
  const out: Action[] = [];
  const off = (a: Action): Action => ({ ...a, disabled: STALE });
  if (places && places.coded > 0) out.push(off(LABELS));
  if (places?.pallets) out.push(off(PALLET_LABELS));
  if (places && places.total > 0) out.push(off(PACKING));
  // ⚠️ только когда в 1С есть что дописать: у поставки с готовыми документами «Записать в 1С»
  // не действие этой стадии вовсе, и гасить нечего
  if (!d.onec) out.push(off(act("onec_run", "Записать в 1С")));
  else if (d.onec.state !== "done")
    out.push(off(act("onec_run", "Продолжить запись в 1С")));
  return out;
}

const LABELS = act(
  "labels",
  "Этикетки коробов",
  "PDF площадки с нашей допечаткой — по одной этикетке на грузоместо",
  true,
);

const PALLET_LABELS = act(
  "pallets",
  "Наклейки палет",
  "«ПАЛЛЕТА №N» и сколько на ней коробов — по транспортным кодам",
  true,
);

/** Стадии, на которых состав уже согласован площадкой и правится только режимом изменений.
 *
 *  ⚠️ `ordering` тут нет намеренно: заявки ещё нет, править нечего. `onec_partial` — тоже: цикл
 *  1С на полпути, и каскад упёрся бы в него уже после удаления грузомест (тот же гард стоит в
 *  бэкенде, `ozon_change.start`).
 */
const CHANGE_STAGES: OzonStage[] = [
  "order",
  "layout",
  "layout_partial",
  "sent",
  "assembly",
];

/** Вход в правку состава. Кодов ещё нет — та же правка, только самая дешёвая: снимать с площадки
 *  нечего, перепечатывать тоже, и места уедут первой отправкой.
 *
 *  ⚠️ Без раскладки пункт ПОГАШЕН, а не спрятан: править состав портал умеет только через места
 *  (уменьшение — это `remove` у строки места), и на пустой раскладке режим открылся бы экраном,
 *  на котором не за что взяться. Дорога отсюда одна — «Разложить», и она же главная кнопка стадии.
 */
function changeStart(places: PlacesInfo): Action {
  const a = act(
    "change_start",
    "Внести изменения в состав",
    places.coded > 0
      ? "Правка состава, уже согласованного площадкой: количества, партии, новые артикулы. " +
          "Само открытие обратимо — наружу ничего не уйдёт до кнопки «Применить изменения»."
      : "Правка состава, согласованного площадкой: количества, партии, новые артикулы. Кодов " +
          "ещё нет — снимать и перепечатывать нечего, места уедут на площадку первой отправкой. " +
          "Само открытие обратимо — наружу ничего не уйдёт до кнопки «Применить изменения».",
  );
  return places.sendable > 0
    ? a
    : { ...a, disabled: "раскладки ещё нет — сначала «Разложить»" };
}

/** «Отправить места» и «Дослать N» — одно действие: площадка принимает и то и другое одной
 *  ручкой, а разное число мест не повод разводить кнопки. */
function sendAction(d: OzonDirection): Action {
  const left = d.places_total - d.places_sent;
  const send =
    d.places_sent === 0
      ? act(
          "send",
          "Отправить места",
          "Площадка выдаст коды — по ним печатаются этикетки, и дальше состав правится только через снятие кодов",
        )
      : act(
          "send",
          `Дослать ${left}`,
          "Отправить места, которых на площадке ещё нет",
        );
  // ⚠️ без номера поставки отправлять некуда: подтверждение спрашивало бы «…в поставку null», а
  // площадке нечего назвать. Старый тулбар в этом случае кнопку просто прятал
  return d.supply_number ? send : { ...send, disabled: "нет номера поставки" };
}

/** Общее «…» для всех стадий после заявки. Порядок один: правка раскладки, печать, аварийное,
 *  отмена заявки последней. */
function rest(
  stage: OzonStage,
  d: OzonDirection,
  places: PlacesInfo | null,
): Action[] {
  const more: Action[] = [];
  // ⚠️ пересчёт затирает ручные правки и все места целиком — пока код не выдан ни одному месту
  if (stage !== "order" && places && places.total > 0 && places.coded === 0)
    more.push(
      act(
        "recompute",
        "Разложить заново",
        "Пересчитать раскладку с нуля: ручные правки будут потеряны",
      ),
    );
  // ⚠️ В режиме изменений «…» — это отмена и погашенные печать с 1С, и больше ничего: править
  // раскладку сейчас можно прямо в таблице, а всё остальное бэкенд отвергнет, пока заход идёт
  if (stage === "change")
    return [
      ...staleWhileChanging(d, places),
      // ⚠️ Полный пересбор — здесь, а не рядом с правкой: он опустошает ВСЕ места с кодами, и
      // каскад пойдёт режимом замены — коды сменятся у всех, этикетки в мусор. Обычное
      // «Перераспределить партии» стоит над таблицей и трогает только то, чего не хватает.
      ...(d.change && editing(d.change) && places && places.total > 0
        ? [
            act(
              "recompute",
              "Разложить заново",
              "Пересобрать раскладку с нуля по текущему составу: ручные правки будут затёрты, " +
                "места с кодами опустеют, и коды перевыпустятся У ВСЕХ мест",
            ),
            act(
              "change_abort",
              "Отменить изменения",
              "Вернуть раскладку такой, какой она уехала на площадку: коды, коробы, партии и " +
                "строки поставки — из снимка, снятого на старте правки",
            ),
          ]
        : []),
      CANCEL,
    ];
  // ⚠️ Вход в режим изменений — ВЕЗДЕ после заявки, а не только там, где состав заморожен
  // кодами. Заявку площадка держит согласованной с первой минуты, и уменьшить её иначе, чем
  // `content/update`, нельзя: «Состав» после заявки закрыт, а обычный редактор раскладки правит
  // только раскладку. Пока пункт стоял на `coded > 0`, поставке с заявкой и без кодов оставалось
  // одно — отменить её и заводить заново с новым окном (прод, 17.09.2026). Битую поставку не
  // зовём: цикл 1С у неё не прочитался, и правка встала бы на шаге документов — уже после
  // удаления грузомест
  if (CHANGE_STAGES.includes(stage) && places && !d.broken)
    more.push(changeStart(places));
  if (stage !== "assembly" && places && places.coded > 0) more.push(LABELS);
  // наклейка палеты печатается по ТРАНСПОРТНОМУ коду, которого коробочная этикетка не знает
  if (places?.pallets) more.push(PALLET_LABELS);
  if (stage !== "order" && places && places.total > 0) more.push(PACKING);
  // ⚠️ перевыпуск меняет коды У ВСЕХ мест, а документы 1С не переписывает вовсе. Поэтому при
  // ЛЮБЫХ документах (`d.onec` или `broken` — у битой цикл не прочитался, и пустой `onec` там не
  // значит «в учёте ничего нет») пункт ПОГАШЕН С ПРИЧИНОЙ, а не спрятан: бэкенд отказывает теми же
  // словами (`ozon_cargoes.release`), и дорога у поставки с документами одна — режим изменений,
  // где партии сверяются с живым остатком, а коды снимает и выдаёт каскад
  if (places && places.sendable > 0 && places.coded === places.sendable) {
    const reissue = act(
      "reissue",
      "Перевыпустить места",
      "Пересобрать партии и отправить состав заново с заменой всего набора: КОДЫ ПОМЕНЯЮТСЯ У ВСЕХ мест",
    );
    more.push(
      d.onec || d.broken
        ? {
            ...reissue,
            disabled:
              "есть документы 1С — правьте через «Внести изменения в состав»",
          }
        : reissue,
    );
  }
  // ⚠️ `d.onec || d.broken`: у битой поставки бэкенд отдаёт `onec=null` и текст поломки в
  // `broken` — и это ровно та поставка, которую этим пунктом чинят. По одному `d.onec` починка
  // пропадала именно тогда, когда без неё не обойтись
  if (d.onec || d.broken)
    more.push(
      act(
        "onec_reset",
        "Забыть документы 1С (после пометки на удаление)",
        "Портал забудет заказ, паллеты, ПТМО и счёт-фактуру и запишет поставку заново. Сами документы ДО этого помечают на удаление в 1С — иначе портал откажет.",
      ),
    );
  more.push(CANCEL);
  return more;
}

/** Один документ в двух форматах — одной строкой меню, где кликается каждый формат. */
const PACKING: Action = {
  ...act(
    "packing",
    "Сборочный лист: PDF",
    "Состав по местам — тот лист, по которому собирают",
    true,
  ),
  alt: act(
    "packing_xlsx",
    "Excel",
    "Тот же состав плоским листом со всеми ключами в каждой строке — под ВПР и сводные",
    true,
  ),
};

const CANCEL = act(
  "cancel",
  "Отменить заявку",
  "Снять заявку с площадки: окно освободится, коды грузомест умрут вместе с заявкой",
);
